import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createStoreCategory,
  getStoreCategories,
  getStoreApiPayload,
  isStoreApiSuccess,
} from '../../services/storeApi';
import { parseCategoriesList, slugifyCategoryKey } from '../Store/storeLabels';
import { useStoreSession } from '../Store/useStoreSession';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

function StoreCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { showSnackbar, handleSessionExpired } = useStoreSession(setSnackbar);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStoreCategories();
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load categories', 'error');
        setCategories([]);
        return;
      }
      setCategories(parseCategoriesList(res));
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const previewKey = key.trim() || (label.trim() ? slugifyCategoryKey(label) : '');

  const handleCreate = async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      showSnackbar('Label is required', 'warning');
      return;
    }

    const body = { label: trimmedLabel };
    if (key.trim()) body.key = key.trim().toUpperCase();
    else body.key = slugifyCategoryKey(trimmedLabel);

    setSaving(true);
    try {
      const res = await createStoreCategory(body);
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to create category', 'error');
        return;
      }
      const created = getStoreApiPayload(res);
      showSnackbar(`Category created: ${created?.label || trimmedLabel} (${created?.key || body.key})`);
      setCreateOpen(false);
      setLabel('');
      setKey('');
      loadCategories();
    } finally {
      setSaving(false);
    }
  };

  const sourceChip = (cat) => {
    const source = (cat.source || cat.type || '').toLowerCase();
    if (source.includes('hubble')) {
      return <Chip size="small" label="Hubble" color="default" />;
    }
    if (source.includes('custom') || source.includes('admin') || source.includes('dugout')) {
      return <Chip size="small" label="Custom" color="primary" />;
    }
    return <Chip size="small" label={cat.source || 'Merch'} variant="outlined" />;
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Store Categories</h3>
                  <Typography variant="body2" color="text.secondary">
                    Hubble categories from brand sync · custom categories for Dugout merch
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button component={Link} to="/admin/store" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Store dashboard
                      </Button>
                      <Button component={Link} to="/admin/store/products" variant="outlined" size="small">
                        Products
                      </Button>
                      <Button variant="contained" onClick={() => setCreateOpen(true)}>
                        Create custom category
                      </Button>
                      <Button variant="text" onClick={loadCategories} disabled={loading}>
                        Refresh
                      </Button>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      Gift card categories come from Hubble after sync. Create custom categories (e.g. Sports, Jerseys)
                      before adding merch — assign <strong>category_key</strong> on each Dugout product.
                    </Alert>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Key</TableCell>
                              <TableCell>Label</TableCell>
                              <TableCell>Source</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {categories.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  No categories yet — sync Hubble brands or create a custom category
                                </TableCell>
                              </TableRow>
                            ) : (
                              categories.map((cat) => (
                                <TableRow key={cat.key} hover>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {cat.key}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{cat.label || cat.key}</TableCell>
                                  <TableCell>{sourceChip(cat)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create custom category</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Label"
                required
                placeholder="Sports"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                helperText="Display name in the app category list"
              />
              <TextField
                label="Key (optional)"
                placeholder="SPORTS"
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
                helperText={previewKey ? `Will use key: ${previewKey}` : 'Auto-generated from label if empty'}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default StoreCategories;
